import * as React from 'react';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import FailureActions, { FailureOverview } from 'failure/actions/FailureActions';
import { Alert, Button, Col, Row } from 'components/graylog';
import { Icon, Spinner } from 'components/common';
import Routes from 'routing/Routes';
import DocsHelper from 'util/DocsHelper';
import { LinkContainer } from 'components/graylog/router';
import { SmallSupportLink, DocumentationLink } from 'components/support';

const FAILURES_STREAM_ID = '000000000000000000000004';

const StatTitle = styled.strong(({ theme }) => css`
  text-transform: capitalize;
  float: none;
  grid-column: 1;
  padding-right: ${theme.spacings.sm};
`);

const StatCount = styled.span<{$enabled: boolean}>(({ $enabled }) => css`
  grid-column: 2;
  font-style: ${$enabled ? 'normal' : 'italic'};
`);

const StyledIcon = styled(Icon)(({ theme }) => css`
  margin-right: ${theme.spacings.xs};
`);

const StyledSmallSupportLink = styled(SmallSupportLink)(({ theme }) => css`
  margin: ${theme.spacings.sm} 0;
`);

function FailureProcessingOverview() {
  const [overviewStats, setOverviewStats] = useState<FailureOverview>();

  useEffect(() => {
    if (!overviewStats) {
      FailureActions.loadFailureProcessingOverview().then((res) => {
        setOverviewStats(res);
      });
    }
  }, [overviewStats]);

  return (
    <Row className="content">
      <Col md={12}>
        <h2>Indexing & Processing Failures</h2>
        <StyledSmallSupportLink>
          Every message that was not successfully indexed or processed will be logged as a failure. You can learn more about this feature in the{' '}
          <DocumentationLink page={DocsHelper.PAGES.INDEXER_FAILURES} text="Graylog Documentation" />.
        </StyledSmallSupportLink>
        <div>
          {!overviewStats ? <Spinner /> : Object.keys(overviewStats).map((key) => {
            const noErrors = overviewStats[key].count === 0;
            const isEnabled = overviewStats[key].enabled;
            const alertStyle = !isEnabled ? 'default' : 'success';

            return (
              <Alert key={key} bsStyle={noErrors ? alertStyle : 'danger'}>
                <StatTitle>
                  <StyledIcon name={noErrors ? 'check-circle' : 'ambulance'} />
                  {key}:
                </StatTitle>
                <StatCount $enabled={overviewStats[key].enabled}>
                  {overviewStats[key].enabled ? `${overviewStats[key].count.toLocaleString()} errors in the past 24 hours` : 'Disabled'}

                  {!noErrors && isEnabled ? (
                    <LinkContainer to={overviewStats[key].source === 'mongodb'
                      ? Routes.SYSTEM.INDICES.FAILURES
                      : Routes.stream_search(FAILURES_STREAM_ID, `failure_type:${key}`, { relative: 86400 })}>
                      <Button bsStyle="info" bsSize="xs" className="pull-right">
                        Show errors
                      </Button>
                    </LinkContainer>
                  ) : null}
                </StatCount>
              </Alert>
            );
          })}
        </div>
      </Col>
    </Row>
  );
}

export default FailureProcessingOverview;
